import type { ProposalProductItemType } from '@econtractor/types';

export interface ReplacedProductItem {
    productId: number;
    prodName: string;
}

export interface MergeProductRowsOptions {
    removeProductIds?: number[];
    removeSetUnitSourceIds?: number[];
}

export interface MergeProductRowsResult {
    products: ProposalProductItemType[];
    replaced: ReplacedProductItem[];
}

const rowLabel = (row: ProposalProductItemType): string =>
    row.prodName || row.sourceName || (row.productId > 0 ? `#${row.productId}` : '');

const replacedKey = (row: ProposalProductItemType): string => {
    if (row.asSetUnit) return `set:${row.sourceId || 0}`;
    return `product:${row.productId}`;
};

const toReplacedItem = (row: ProposalProductItemType): ReplacedProductItem => ({
    productId: row.asSetUnit ? -(row.sourceId || 0) : row.productId,
    prodName: rowLabel(row),
});

const mergeProductRows = (
    existing: ProposalProductItemType[],
    incoming: ProposalProductItemType[],
    opts?: MergeProductRowsOptions,
): MergeProductRowsResult => {
    const idsToRemove = new Set<number>(opts?.removeProductIds || []);
    const setUnitIdsToRemove = new Set<number>(opts?.removeSetUnitSourceIds || []);

    for (const row of incoming) {
        if (row.productId > 0) idsToRemove.add(row.productId);
        if (row.asSetUnit && row.sourceId) setUnitIdsToRemove.add(row.sourceId);
    }

    const replacedMap = new Map<string, ReplacedProductItem>();
    const filtered: ProposalProductItemType[] = [];

    for (const row of existing) {
        const removeByProduct = row.productId > 0 && idsToRemove.has(row.productId);
        const removeBySetUnit =
            !!row.asSetUnit && !!row.sourceId && setUnitIdsToRemove.has(row.sourceId);

        if (removeByProduct || removeBySetUnit) {
            const key = replacedKey(row);
            if (!replacedMap.has(key)) replacedMap.set(key, toReplacedItem(row));
            continue;
        }
        filtered.push(row);
    }

    return {
        products: [...filtered, ...incoming],
        replaced: Array.from(replacedMap.values()),
    };
};

export default mergeProductRows;
